import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FinePrint } from "@/components/FinePrint";
import { Loader2, Lightbulb } from "lucide-react";

const STATUSES = ["submitted", "under_review", "needs_info", "accepted", "planned", "in_development", "implemented", "declined", "duplicate"];
const LABEL = { submitted: "Submitted", under_review: "Under Review", needs_info: "Needs Info", accepted: "Accepted", planned: "Planned", in_development: "In Development", implemented: "Implemented", declined: "Declined", duplicate: "Duplicate" };
const CLS = { submitted: "bg-slate-100 text-slate-600", under_review: "bg-sky-100 text-sky-800", needs_info: "bg-amber-100 text-amber-800", accepted: "bg-teal-100 text-teal-800", planned: "bg-indigo-100 text-indigo-800", in_development: "bg-purple-100 text-purple-800", implemented: "bg-emerald-100 text-emerald-800", declined: "bg-red-100 text-red-700", duplicate: "bg-slate-100 text-slate-400" };

export function AdminSuggestions() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("all");
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/suggestions", { params: { status } }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const setStat = async (s, newStatus) => {
    setBusy(true);
    try { await api.patch(`/admin/suggestions/${s.id}`, { status: newStatus }); toast.success("Status updated"); load(); if (detail?.id === s.id) setDetail({ ...detail, status: newStatus }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { const { data } = await api.patch(`/admin/suggestions/${detail.id}`, { internal_note: note }); setDetail(data); setNote(""); toast.success("Note added"); }
    catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <DashboardShell title="Suggestions" subtitle="Development-feedback pipeline from GPA+ users" nav={ADMIN_NAV}>
      <div className="mb-4 flex items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" data-testid="sugg-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{LABEL[s]}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {!rows && <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#003057]" /></div>}
      {rows && (
        <Card className="p-2 card-elevate sm:p-4">
          <Table>
            <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>From</TableHead><TableHead>Submitted</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Review</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} data-testid={`sugg-row-${s.id}`}>
                  <TableCell className="font-semibold text-[#003057]"><button className="hover:underline" onClick={() => { setDetail(s); setNote(""); }}>{s.title}</button></TableCell>
                  <TableCell className="text-sm text-slate-500">{s.category}</TableCell>
                  <TableCell className="text-xs text-slate-500">{s.submitter_email}<br /><span className="capitalize text-slate-400">{s.submitter_role}</span></TableCell>
                  <TableCell className="font-mono-num text-xs text-slate-400">{s.created_at?.slice(0, 10)}</TableCell>
                  <TableCell><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${CLS[s.status]}`}>{LABEL[s.status]}</span></TableCell>
                  <TableCell className="text-right">
                    <Select value={s.status} onValueChange={(v) => setStat(s, v)} disabled={busy}>
                      <SelectTrigger className="w-40" data-testid={`sugg-status-${s.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((st) => <SelectItem key={st} value={st}>{LABEL[st]}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-400" data-testid="sugg-admin-empty">No suggestions yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      )}
      <FinePrint>Suggestions are proposals — reviewing or accepting one does not automatically create a development requirement or change GPA+ methodology.</FinePrint>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="sugg-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading text-lg text-[#003057]"><Lightbulb className="h-5 w-5 text-[#78be21]" />{detail.title}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <p className="text-slate-500">{detail.category} · from {detail.submitter_email} ({detail.submitter_role})</p>
                <p className="rounded-lg bg-slate-50 p-3 text-slate-700">{detail.description}</p>
                {detail.why && <p className="text-slate-600"><b>Why:</b> {detail.why}</p>}
                <p className="text-xs text-slate-400">Submitted from: {detail.page || "—"}</p>
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-400">Internal notes (admin only)</p>
                  {(detail.internal_notes || []).map((n, i) => <p key={i} className="rounded bg-slate-50 px-2 py-1 text-xs text-slate-600">{n.author}: {n.text}</p>)}
                  <div className="mt-2 flex gap-2"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add internal note…" data-testid="sugg-note-input" /><Button variant="outline" onClick={addNote} disabled={busy}>Add</Button></div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
